import { PlusOutlined } from '@ant-design/icons';
import { Button, Input, Space, Table, message } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { msgid, ngettext, t } from 'ttag';

import DeleteBar from 'common/components/DeleteBar';
import DeleteModal from 'common/components/Modal/DeleteModal';
import useSyncWithUrlSearchParams from 'common/hooks/useSyncWithUrlSearchParams';

import CreateModal from './CreateModal';
import { useDeleteMultipleStaffMutation, useStaffListQuery } from './queries';

const { Search } = Input;

const SearchBox = styled(Search)`
  && {
    width: 350px;
  }
`;

const Staff = () => {
  const [query] = useSyncWithUrlSearchParams();
  const [search, setSearch] = useState(query.search || '');
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const { data, isLoading } = useStaffListQuery();
  const deleteMultipleMutation = useDeleteMultipleStaffMutation();

  const filteredData = useMemo(
    () =>
      (data || []).filter(item =>
        `${item.firstName} ${item.lastName} ${item.email}`
          .toLowerCase()
          .includes((search || '').toLowerCase()),
      ),
    [data, search],
  );

  // Clear selected row keys when search changes
  useEffect(() => {
    setSelectedRowKeys([]);
  }, [search]);

  const handleDeleteSelected = () => {
    const count = selectedRowKeys.length;

    DeleteModal.confirm({
      label: {
        singular: t`Staff Member`,
        plural: t`Staff Members`,
      },
      count,
      async onDelete() {
        await deleteMultipleMutation.mutateAsync(selectedRowKeys, {
          onSuccess: () => {
            setSelectedRowKeys([]);
            message.success(t`Staff members removed successfully`);
          },
          onError: () => {
            message.error(t`Failed to remove staff members. Please try again.`);
          },
        });
      },
    });
  };

  return (
    <>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBlock: 20 }}>
        <Space>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setIsCreateModalVisible(true)}
          >
            {t`Add Staff Member`}
          </Button>
        </Space>
        <SearchBox
          defaultValue={search}
          placeholder={t`Search Staff...`}
          disabled={isLoading}
          onChange={e => setSearch(e.target.value)}
        />
      </Space>

      <Table
        rowKey="id"
        dataSource={filteredData}
        loading={isLoading}
        rowSelection={{
          selectedRowKeys,
          onChange: setSelectedRowKeys,
        }}
        columns={[
          {
            key: 'name',
            title: t`Name`,
            render: (_, record) => `${record.firstName} ${record.lastName}`,
            sorter: (a, b) =>
              `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`),
          },
          {
            key: 'email',
            dataIndex: 'email',
            title: t`Email Address`,
            sorter: (a, b) => a.email.localeCompare(b.email),
            filterSearch: true,
            width: 400,
          },
        ]}
      />

      <DeleteBar
        style={{ left: 0 }}
        open={selectedRowKeys.length > 0}
        buttonText={ngettext(
          msgid`Remove ${selectedRowKeys.length} Staff Member`,
          `Remove ${selectedRowKeys.length} Staff Members`,
          selectedRowKeys.length,
        )}
        onClick={handleDeleteSelected}
      />

      <CreateModal visible={isCreateModalVisible} onClose={() => setIsCreateModalVisible(false)} />
    </>
  );
};

export default Staff;
